import { useEffect, useMemo, useState } from "react";
import { useAction, useQuery } from "convex/react";

import { useScreen0Language } from "@/features/language";
import type { AppIconInput } from "./appIcon";
import {
  buildAppStoreLookupCountries,
  shouldRetryAppStoreNotFoundLookup,
} from "./appStoreCountries";
import {
  appStoreLookupBundleId,
  type AppStoreMetadata,
} from "./appStoreLookup";
import { actionRef, queryRef } from "../convex/refs";

const getAppStoreMetadataRef = queryRef<{ bundleId: string }, AppStoreMetadata | null>(
  "appStoreMetadata:getByBundleId",
);
const resolveAppStoreMetadataRef = actionRef<
  { bundleId: string; countries?: string[] },
  AppStoreMetadata | null
>("appStoreMetadata:resolve");

// Dedup concurrent iTunes lookups for the same bundle ID across every mounted
// AppIcon / name consumer. Once a bundle ID is in flight or known-missed we skip
// re-triggering the action (the Convex query subscription delivers the result).
const inFlightResolves = new Set<string>();

export type AppStoreMetadataHookResult = {
  metadata: AppStoreMetadata | null | undefined;
  /** True after an online resolve attempt finished without writing a cache row. */
  resolveFailed: boolean;
  isOnline: boolean;
};

/**
 * Cache-first App Store metadata for an app row. Reads the Convex cache via a
 * live query; on a miss (no cached row) it triggers the iTunes lookup action
 * once, when online. Returns `undefined` while the cache query is loading,
 * `null` when the row is not eligible or not cached yet.
 */
export function useAppStoreMetadata(input: AppIconInput): AppStoreMetadataHookResult {
  const { languageCode } = useScreen0Language();
  const countries = useMemo(
    () => buildAppStoreLookupCountries(languageCode),
    [languageCode],
  );
  const bundleId = appStoreLookupBundleId(input);
  const cached = useQuery(getAppStoreMetadataRef, bundleId ? { bundleId } : "skip");
  const resolveMetadata = useAction(resolveAppStoreMetadataRef);
  const [isOnline, setIsOnline] = useState(
    () => typeof navigator === "undefined" || navigator.onLine,
  );
  const [resolveFailed, setResolveFailed] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (cached !== null && cached !== undefined) {
      setResolveFailed(false);
    }
  }, [cached]);

  useEffect(() => {
    // Trigger a lookup on cache miss, when a found row has no icon source yet
    // (storage backfill incomplete and no legacy iTunes URL to bridge), or when
    // a stale us-only negative cache can be retried with more storefronts.
    if (!bundleId || !isOnline || cached === undefined) return;

    const isCacheMiss = cached === null;
    const needsIconRepair = cached?.lookupStatus === "found" && !cached.iconUrl;
    const needsNotFoundRetry =
      cached?.lookupStatus === "not_found" &&
      shouldRetryAppStoreNotFoundLookup(cached.lookupCountriesTried, countries);
    if (!isCacheMiss && !needsIconRepair && !needsNotFoundRetry) return;
    if (inFlightResolves.has(bundleId)) return;

    inFlightResolves.add(bundleId);
    let cancelled = false;
    void resolveMetadata({ bundleId, countries })
      .then((result) => {
        if (!cancelled && !result) setResolveFailed(true);
      })
      .catch(() => {
        if (!cancelled) setResolveFailed(true);
      })
      .finally(() => {
        if (!cancelled) inFlightResolves.delete(bundleId);
      });

    return () => {
      cancelled = true;
      inFlightResolves.delete(bundleId);
    };
  }, [bundleId, countries, isOnline, cached, resolveMetadata]);

  return {
    metadata: bundleId ? cached : null,
    resolveFailed,
    isOnline,
  };
}
